import { ChartConfiguration } from 'chart.js';

/**
 * Pure Chart.js configuration transforms used by the statistics export pipeline.
 *
 * These were extracted out of StatisticsExportCaptureService so the config-shaping
 * logic can be unit-tested in isolation (no DOM, no Chart.js instance, no DI).
 * The service keeps thin delegating methods so its public API is unchanged.
 */

/**
 * Reset Chart.js options that applyChartExportLayout may leave on shared singleton
 * configs. Call before creating a new Chart from chart-configs.shared.ts after PDF
 * capture, otherwise export-only layout overrides leak into the live chart.
 */
export function resetSharedBarChartOptions(config: ChartConfiguration<'bar'>): void {
  if (!config.options) {
    return;
  }

  config.options.responsive = true;
  config.options.maintainAspectRatio = false;
  delete (config.options as Record<string, unknown>)['animation'];
  delete (config.options as Record<string, unknown>)['devicePixelRatio'];

  if (config.options.indexAxis !== 'y' && config.options.scales?.['y']) {
    (config.options.scales['y'] as { beginAtZero?: boolean }).beginAtZero = true;
  }
}

/** Build a stacked-bar export config from item series, cycling the colour palette. */
export function buildStackedBarExportConfig(
  items: Array<{ label: string; data: (number | null)[] }>,
  labels: string[],
  baseConfig: ChartConfiguration<'bar'>,
  barThickness: number,
  colorPalette: string[]
): ChartConfiguration<'bar'> {
  let colorIndex = 0;
  const pickColor = (): string => {
    const color = colorPalette[colorIndex];
    colorIndex = colorIndex === colorPalette.length - 1 ? 0 : colorIndex + 1;
    return color;
  };

  const datasets = items.map((item) => ({
    label: item.label,
    data: [...item.data],
    barThickness,
    backgroundColor: pickColor(),
  }));

  return {
    type: 'bar',
    data: { labels: [...labels], datasets },
    options: { ...baseConfig.options, responsive: true, maintainAspectRatio: false },
    plugins: baseConfig.plugins,
  };
}

/**
 * Deep-enough copy so export layout never mutates the caller's config object.
 *
 * Beyond the top-level options, the nested objects the export pipeline can resize
 * — per-axis `ticks` (incl. `ticks.font`) and `grid`, and `plugins.legend.labels.font`
 * / `plugins.datalabels.font` — are copied too, so font/typography tweaks during export
 * never leak back into a shared singleton config or a live chart.
 * Function values (e.g. `ticks.callback`, tooltip callbacks) are intentionally shared:
 * they are invoked, never mutated.
 */
export function cloneChartConfiguration(config: ChartConfiguration): ChartConfiguration {
  return {
    type: config.type,
    data: {
      labels: config.data?.labels ? [...(config.data.labels as unknown[])] : [],
      datasets: (config.data?.datasets ?? []).map((dataset) => ({
        ...dataset,
        data: Array.isArray(dataset.data) ? [...dataset.data] : dataset.data,
      })),
    },
    options: config.options
      ? {
          ...config.options,
          responsive: true,
          maintainAspectRatio: config.options.maintainAspectRatio ?? false,
          scales: config.options.scales
            ? (Object.fromEntries(
                Object.entries(config.options.scales).map(([axisKey, axisConfig]) => [
                  axisKey,
                  cloneAxisConfig(axisConfig),
                ])
              ) as any)
            : undefined,
          plugins: clonePluginsConfig(config.options.plugins),
        }
      : undefined,
    plugins: config.plugins,
  };
}

function cloneFontConfig(font: unknown): unknown {
  return font && typeof font === 'object' ? { ...(font as object) } : font;
}

function cloneAxisConfig(axis: unknown): unknown {
  if (!axis || typeof axis !== 'object') {
    return axis;
  }
  const a = axis as Record<string, any>;
  const copy: Record<string, any> = { ...a };
  if (a['ticks']) {
    copy['ticks'] = { ...a['ticks'], font: cloneFontConfig(a['ticks'].font) };
  }
  if (a['grid']) {
    copy['grid'] = { ...a['grid'] };
  }
  return copy;
}

function clonePluginsConfig(plugins: unknown): any {
  if (!plugins || typeof plugins !== 'object') {
    return undefined;
  }
  const p = plugins as Record<string, any>;
  const copy: Record<string, any> = { ...p };
  if (p['legend']) {
    copy['legend'] = { ...p['legend'] };
    if (p['legend'].labels) {
      copy['legend'].labels = {
        ...p['legend'].labels,
        font: cloneFontConfig(p['legend'].labels.font),
      };
    }
  }
  if (p['datalabels']) {
    copy['datalabels'] = { ...p['datalabels'], font: cloneFontConfig(p['datalabels'].font) };
  }
  if (p['tooltip']) {
    copy['tooltip'] = { ...p['tooltip'] };
  }
  return copy;
}
